import { useMemo, useState, useEffect } from "react";
import { useLocation } from "wouter";
import { ChevronDown, FileDown, Home, LogIn, LogOut, Shield, GraduationCap, BookOpen, CheckCircle2, Compass, ExternalLink } from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { TabContent, TabFolder, DecisionTree } from "@/types/cms";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";

// Curated sidebar sequence by folder slug. Folders not in this list won't render.
const TPA_RESOURCES_SLUG = 'folder-1771484890761';
const PM_FOLDER_SLUGS = ['resources-pm'];
// Note: Downloads is no longer a folder. It's a virtual aggregator showing all
// visible tabs that have a file_url, alphabetised, regardless of which folder
// they live in. Eliminates folder-id duplication while still giving users a
// single "where are the PDFs" view.

interface LearningModule {
  id: string;
  title: string;
  sort_order: number;
  is_published: boolean;
}

interface LearningProgress {
  module_id: string;
  completed: boolean;
}

interface SidebarNavigationProps {
  folders: TabFolder[];
  tabs: TabContent[];
  activeTab: string;
  setActiveTab: (tabId: string) => void;
  showWelcome?: boolean;
  onShowWelcome?: () => void;
  activeLearningModule?: string | null;
  onSelectLearningModule?: (moduleId: string) => void;
  decisionTrees?: DecisionTree[];
  activeTreeId?: string | null;
  onSelectTree?: (tree: DecisionTree) => void;
  // When set, the matching folder accordion auto-expands and its label
  // shows the active indicator — without highlighting any child tab.
  activeFolderId?: string | null;
  // Same idea for the Learning Program section (module-based, not a folder).
  learningProgramActive?: boolean;
  // When the user clicks the "Find downloads" tile on the welcome page, the
  // parent sets this true; the sidebar then auto-expands the Downloads section.
  downloadsActive?: boolean;
}

const activeClass = "border-l-3 border-sidebar-primary bg-sidebar-accent text-sidebar-accent-foreground font-medium min-h-10 h-auto py-2.5 leading-tight";
const inactiveClass = "text-sidebar-foreground/85 hover:bg-transparent hover:text-sidebar-foreground hover:ring-2 hover:ring-sidebar-primary min-h-10 h-auto py-2.5 leading-tight";
const groupLabelClass = "text-xs font-bold uppercase tracking-wide text-sidebar-foreground/60 cursor-pointer hover:text-sidebar-foreground/80 transition-colors flex items-center justify-between group";
// Applied to a folder label when it's the active section (user clicked "TPA
// Resources" from the welcome page). Matches the tab active look: orange
// accent bar + accent background.
const activeLabelClass = "text-xs font-bold uppercase tracking-wide text-sidebar-accent-foreground cursor-pointer transition-colors flex items-center justify-between group border-l-3 border-sidebar-primary bg-sidebar-accent pl-2";
// TPA Resources gets emphasis: larger text, brighter color, left accent bar in the ACRRM orange
const tpaResourcesLabelClass = "text-sm font-bold uppercase tracking-wide text-sidebar-foreground cursor-pointer hover:opacity-80 transition-opacity flex items-center justify-between group border-l-3 border-[var(--color-acrrm-orange)] pl-2";
// Active variant for TPA Resources (same orange accent but with accent bg + brighter text)
const tpaResourcesActiveLabelClass = "text-sm font-bold uppercase tracking-wide text-sidebar-accent-foreground cursor-pointer hover:opacity-80 transition-opacity flex items-center justify-between group border-l-3 border-[var(--color-acrrm-orange)] bg-sidebar-accent pl-2";
// Practice Manager Resources section heading (non-clickable, just a divider/label)
const sectionDividerClass = "text-xs font-bold uppercase tracking-wider text-sidebar-foreground/50 pt-4 pb-1 px-2 border-t border-sidebar-border mt-2";

export function SidebarNavigation({
  folders,
  tabs,
  activeTab,
  setActiveTab,
  showWelcome,
  onShowWelcome,
  activeLearningModule,
  onSelectLearningModule,
  decisionTrees = [],
  activeTreeId,
  onSelectTree,
  activeFolderId,
  learningProgramActive,
  downloadsActive,
}: SidebarNavigationProps) {
  const { isAuthenticated, profile, signOut } = useAuth();
  const [, setLocation] = useLocation();

  // Learning modules and progress (only fetched when authenticated)
  const [learningModules, setLearningModules] = useState<LearningModule[]>([]);
  const [learningProgress, setLearningProgress] = useState<LearningProgress[]>([]);

  // Single accordion: only ONE section open at a time. Sidebar real estate is
  // limited and users get lost when multiple sections stay sticky-open.
  // Value is either a folder UUID, or one of the section keys below.
  const SECTION_LEARNING = "learning";
  const SECTION_GUIDES = "guides";
  const SECTION_EXTERNAL = "external";
  const SECTION_DOWNLOADS = "downloads";
  const [openSection, setOpenSection] = useState<string | null>(null);

  const expandedFolder = openSection;
  const learningExpanded = openSection === SECTION_LEARNING;
  const guidesExpanded = openSection === SECTION_GUIDES;
  const externalExpanded = openSection === SECTION_EXTERNAL;
  const downloadsExpanded = openSection === SECTION_DOWNLOADS;

  // When the parent navigates to a section heading (not a specific tab),
  // auto-expand the matching accordion so the user can see what's inside.
  useEffect(() => {
    if (activeFolderId) setOpenSection(activeFolderId);
  }, [activeFolderId]);
  useEffect(() => {
    if (learningProgramActive) setOpenSection(SECTION_LEARNING);
  }, [learningProgramActive]);
  useEffect(() => {
    if (downloadsActive) setOpenSection(SECTION_DOWNLOADS);
  }, [downloadsActive]);

  useEffect(() => {
    if (!isAuthenticated || !supabase) return;
    supabase
      .from('tpa_learning_modules')
      .select('id, title, sort_order, is_published')
      .eq('is_published', true)
      .order('sort_order')
      .then(({ data }) => { if (data) setLearningModules(data); });
    supabase
      .from('tpa_learning_progress')
      .select('module_id, completed')
      .then(({ data }) => { if (data) setLearningProgress(data); });
  }, [isAuthenticated]);

  const completedModules = useMemo(
    () => new Set(learningProgress.filter(p => p.completed).map(p => p.module_id)),
    [learningProgress]
  );

  // Group tabs by folder, excluding external_url tabs (they go in the ACRRM web pages section)
  const tabsByFolder = useMemo(() => {
    const map = new Map<string | null, TabContent[]>();
    tabs.forEach((tab) => {
      if (tab.external_url) return; // External URL tabs are rendered separately
      const key = tab.folder_id ?? null;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(tab);
    });
    map.forEach((folderTabs) => {
      folderTabs.sort((a, b) => a.label.localeCompare(b.label));
    });
    return map;
  }, [tabs]);

  // External URL tabs (ACRRM.org.au web pages) sorted alphabetically
  const externalTabs = useMemo(() => {
    return tabs
      .filter(t => t.external_url)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [tabs]);

  // Visible decision trees (filtered by auth)
  const visibleTrees = useMemo(() => {
    return decisionTrees
      .filter(t => t.is_visible && (!t.requires_auth || isAuthenticated))
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [decisionTrees, isAuthenticated]);

  // Look up specific folders by slug
  const folderBySlug = useMemo(() => {
    const map = new Map<string, TabFolder>();
    folders.forEach(f => map.set(f.slug, f));
    return map;
  }, [folders]);

  const tpaResourcesFolder = folderBySlug.get(TPA_RESOURCES_SLUG);
  const pmFolders = PM_FOLDER_SLUGS.map(slug => folderBySlug.get(slug)).filter(Boolean) as TabFolder[];

  // Virtual Downloads aggregator: every visible tab with a file_url, alphabetised.
  // Audience-aware: unauthenticated (PM) view excludes tabs marked requires_auth.
  // Authenticated (TPA) view sees everything they're allowed to see (which the
  // visibleTabs prop already filters at the parent level).
  const downloadableTabs = useMemo(() => {
    return tabs
      .filter(t =>
        t.is_visible !== false &&
        t.file_url &&
        !t.external_url &&
        (isAuthenticated || !t.requires_auth)
      )
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [tabs, isAuthenticated]);

  const toggleFolder = (folderId: string) => {
    setOpenSection(prev => prev === folderId ? null : folderId);
  };
  const toggleSection = (section: string) => {
    setOpenSection(prev => prev === section ? null : section);
  };

  const isAdmin = profile?.role === 'internal_admin' || profile?.role === 'acrrm_admin';

  // Render a collapsible folder accordion
  const renderFolderAccordion = (folder: TabFolder, emphasise = false) => {
    const folderTabs = tabsByFolder.get(folder.id) || [];
    if (folderTabs.length === 0) return null;
    const isExpanded = expandedFolder === folder.id;
    const isActiveFolder = activeFolderId === folder.id;
    const labelClass = emphasise
      ? (isActiveFolder ? tpaResourcesActiveLabelClass : tpaResourcesLabelClass)
      : (isActiveFolder ? activeLabelClass : groupLabelClass);
    return (
      <SidebarGroup key={folder.id}>
        <SidebarGroupLabel
          onClick={() => toggleFolder(folder.id)}
          className={labelClass}
        >
          <span>{folder.label}</span>
          <ChevronDown
            className={`h-4 w-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
          />
        </SidebarGroupLabel>
        {isExpanded && (
          <div className="max-h-64 overflow-y-auto overscroll-contain pr-1">
            <SidebarMenu>
              {folderTabs.map((tab) => (
                <SidebarMenuItem key={tab.id}>
                  <SidebarMenuButton
                    onClick={() => setActiveTab(tab.id)}
                    isActive={activeTab === tab.id}
                    className={activeTab === tab.id ? activeClass : inactiveClass}
                  >
                    {tab.label}
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </div>
        )}
      </SidebarGroup>
    );
  };

  return (
    <Sidebar>
      <SidebarContent>
        {/* 1. Home (was "Welcome" — renamed for consistency with the new Home button */}
        {/* in the top header bar, which provides always-visible nav back here.) */}
        {onShowWelcome && (
          <SidebarGroup>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={onShowWelcome}
                  isActive={showWelcome}
                  className={showWelcome ? activeClass : inactiveClass}
                >
                  <Home className="h-4 w-4 mr-2" />
                  Home
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroup>
        )}

        {/* 2. TPA Resources (emphasised) - authenticated users only */}
        {isAuthenticated && tpaResourcesFolder && renderFolderAccordion(tpaResourcesFolder, true)}

        {/* 3. Learning Program (authenticated users only, directly under TPA Resources) */}
        {isAuthenticated && learningModules.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel
              onClick={() => toggleSection(SECTION_LEARNING)}
              className={learningProgramActive && !activeLearningModule ? activeLabelClass : groupLabelClass}
            >
              <span className="flex items-center gap-1.5">
                <GraduationCap className="h-3.5 w-3.5" />
                Learning Program
              </span>
              <ChevronDown
                className={`h-4 w-4 transition-transform duration-200 ${learningExpanded ? 'rotate-180' : ''}`}
              />
            </SidebarGroupLabel>
            {learningExpanded && (
              <SidebarMenu>
                {learningModules.map((mod) => {
                  const isActive = activeLearningModule === mod.id;
                  const isCompleted = completedModules.has(mod.id);
                  return (
                    <SidebarMenuItem key={mod.id}>
                      <SidebarMenuButton
                        onClick={() => onSelectLearningModule?.(mod.id)}
                        isActive={isActive}
                        className={isActive ? activeClass : inactiveClass}
                      >
                        <span className="flex items-center gap-2 w-full">
                          {isCompleted ? (
                            <CheckCircle2 className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                          ) : (
                            <BookOpen className="h-3.5 w-3.5 flex-shrink-0 opacity-50" />
                          )}
                          <span className="truncate">{mod.title}</span>
                        </span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            )}
          </SidebarGroup>
        )}

        {/* 4. Downloads — virtual aggregator showing every visible tab with a PDF, alphabetised. */}
        {downloadableTabs.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel
              onClick={() => toggleSection(SECTION_DOWNLOADS)}
              className={groupLabelClass}
            >
              <span className="flex items-center gap-1.5">
                <FileDown className="h-3.5 w-3.5" />
                Downloads
              </span>
              <ChevronDown
                className={`h-4 w-4 transition-transform duration-200 ${downloadsExpanded ? 'rotate-180' : ''}`}
              />
            </SidebarGroupLabel>
            {downloadsExpanded && (
              <div className="max-h-64 overflow-y-auto overscroll-contain pr-1">
                <SidebarMenu>
                  {downloadableTabs.map((tab) => {
                    const isActive = activeTab === tab.id;
                    return (
                      <SidebarMenuItem key={tab.id}>
                        <SidebarMenuButton
                          onClick={() => setActiveTab(tab.id)}
                          isActive={isActive}
                          className={isActive ? activeClass : inactiveClass}
                        >
                          <span className="truncate">{tab.label}</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </div>
            )}
          </SidebarGroup>
        )}

        {/* 5. Training Post Resources (consolidated PM-facing folder; visible to all users) */}
        {pmFolders.map(folder => renderFolderAccordion(folder))}

        {/* 7. Interactive Guides */}
        {visibleTrees.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel
              onClick={() => toggleSection(SECTION_GUIDES)}
              className={groupLabelClass}
            >
              <span className="flex items-center gap-1.5">
                <Compass className="h-3.5 w-3.5" />
                Interactive Guides
              </span>
              <ChevronDown
                className={`h-4 w-4 transition-transform duration-200 ${guidesExpanded ? 'rotate-180' : ''}`}
              />
            </SidebarGroupLabel>
            {guidesExpanded && (
              <div className="max-h-64 overflow-y-auto overscroll-contain pr-1">
                <SidebarMenu>
                  {visibleTrees.map(tree => (
                    <SidebarMenuItem key={tree.id}>
                      <SidebarMenuButton
                        onClick={() => onSelectTree?.(tree)}
                        isActive={activeTreeId === tree.id}
                        className={activeTreeId === tree.id ? activeClass : inactiveClass}
                      >
                        <span className="flex items-center gap-2 w-full">
                          <span className="text-base flex-shrink-0">{tree.icon}</span>
                          <span className="truncate">{tree.title}</span>
                        </span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </div>
            )}
          </SidebarGroup>
        )}

        {/* 8. ACRRM web pages (external URL tabs) */}
        {externalTabs.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel
              onClick={() => toggleSection(SECTION_EXTERNAL)}
              className={groupLabelClass}
            >
              <span className="flex items-center gap-1.5">
                <ExternalLink className="h-3.5 w-3.5" />
                ACRRM web pages
              </span>
              <ChevronDown
                className={`h-4 w-4 transition-transform duration-200 ${externalExpanded ? 'rotate-180' : ''}`}
              />
            </SidebarGroupLabel>
            {externalExpanded && (
              <div className="max-h-64 overflow-y-auto overscroll-contain pr-1">
                <SidebarMenu>
                  {externalTabs.map(tab => (
                    <SidebarMenuItem key={tab.id}>
                      <SidebarMenuButton
                        onClick={() => setActiveTab(tab.id)}
                        isActive={activeTab === tab.id}
                        className={activeTab === tab.id ? activeClass : inactiveClass}
                      >
                        {tab.label}
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </div>
            )}
          </SidebarGroup>
        )}
      </SidebarContent>

      {/* Auth footer */}
      <SidebarFooter className="border-t border-sidebar-border p-3">
        {isAuthenticated ? (
          <div className="space-y-2">
            <p className="text-xs text-sidebar-foreground/50 truncate px-1">
              {profile?.email}
            </p>
            <div className="flex gap-1">
              {isAdmin && (
                <button
                  onClick={() => setLocation("/admin")}
                  className="flex items-center gap-1.5 px-2 py-1.5 rounded text-xs text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/20 transition-colors flex-1"
                >
                  <Shield className="h-3 w-3" />
                  Admin
                </button>
              )}
              <button
                onClick={async () => { await signOut(); setLocation("/"); }}
                className="flex items-center gap-1.5 px-2 py-1.5 rounded text-xs text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent/20 transition-colors flex-1"
              >
                <LogOut className="h-3 w-3" />
                Sign Out
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setLocation("/login")}
            className="flex items-center gap-2 px-2 py-1.5 rounded text-xs text-sidebar-foreground/50 hover:text-sidebar-foreground hover:bg-sidebar-accent/20 transition-colors w-full"
          >
            <LogIn className="h-3 w-3" />
            Team Login
          </button>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
